import { motion } from "framer-motion";
import { Linkedin, Twitter, Mail, Sparkles } from "lucide-react";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_echowings-premium/artifacts/zurefzue_image.png";

export default function Founder() {
  return (
    <section
      data-testid="founder-section"
      className="section relative overflow-hidden"
    >
      <div className="absolute -top-40 right-0 w-[40rem] h-[40rem] radial-glow opacity-40 pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[32rem] h-[32rem] radial-glow opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Founder image */}
        <motion.div
          initial={{ opacity: 0, x: -28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
          data-testid="founder-image-wrap"
        >
          <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/30 to-cyan-400/20 rounded-[2rem] blur-2xl" />
          <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,85,255,0.25)]">
            <img
              src={FOUNDER_IMG}
              alt="Matthew Burgess, Founder & CEO of EchoWings Media"
              className="w-full h-auto block aspect-square object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

            {/* Name plate overlay */}
            <div className="absolute left-5 right-5 bottom-5 glass-card rounded-2xl px-5 py-4 flex items-center justify-between">
              <div>
                <div className="font-display text-lg font-semibold text-white">
                  Matthew Burgess
                </div>
                <div className="text-xs text-cyan-300 uppercase tracking-widest font-semibold">
                  Founder &amp; CEO
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="#"
                  data-testid="founder-social-linkedin"
                  aria-label="LinkedIn"
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                >
                  <Linkedin className="w-3.5 h-3.5" />
                </a>
                <a
                  href="#"
                  data-testid="founder-social-twitter"
                  aria-label="Twitter"
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                >
                  <Twitter className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Founder content */}
        <motion.div
          initial={{ opacity: 0, x: 28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            Meet The Founder
          </div>

          <h2
            className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            data-testid="founder-headline"
          >
            Built by an operator,
            <br />
            <span className="text-gradient-brand">not an account manager.</span>
          </h2>

          <p className="mt-6 text-lg text-white/65 leading-relaxed max-w-2xl">
            Matthew Burgess founded EchoWings Media after a decade of growing
            service businesses from the inside — building websites, ranking
            them on Google, and now teaching them to win in the AI era. Every
            engagement at EchoWings is led personally by Matthew, with
            measurable revenue impact as the only success metric that matters.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            {[
              { label: "10+ Years", desc: "Hands-on growth experience" },
              { label: "150+ Brands", desc: "Personally guided" },
              { label: "$25M+", desc: "Tracked client revenue" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-white/10 bg-white/[0.025] px-5 py-4"
              >
                <div className="font-display text-xl font-bold text-gradient-brand">
                  {s.label}
                </div>
                <div className="text-xs text-white/55 mt-1">{s.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#audit"
              data-testid="founder-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.45)] transition-all duration-300"
            >
              Work With Matthew
            </a>
            <a
              href="mailto:matthew@echowingsmedia.com"
              data-testid="founder-cta-email"
              className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all duration-300 backdrop-blur-md"
            >
              <Mail className="w-4 h-4" />
              matthew@echowingsmedia.com
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
